'use client';

import { Phone, ArrowRight } from 'lucide-react';
import { AppLink } from '@/lib/appLink';
import { useSiteSettings } from '@/lib/CmsContext';

function trimValue(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Centered CTA card (region / portfolio) — matches GRV design screenshot.
 *
 * @param {{ section?: import('@/types/cms').CtaCardPageSection }} props
 */
export default function CtaCard({ section = {} }) {
  const { phoneTel } = useSiteSettings();

  const eyebrow = trimValue(section.eyebrow) || 'Готові розпочати?';
  const title = trimValue(section.title) || 'Безкоштовна консультація';
  const text = trimValue(section.text);
  const primaryLabel = trimValue(section.primary_button_label) || 'Зателефонувати';
  const primaryHref =
    trimValue(section.primary_button_href) ||
    (phoneTel ? `tel:${phoneTel}` : '/contacts');
  const secondaryLabel = trimValue(section.secondary_button_label);
  const secondaryHref = trimValue(section.secondary_button_href) || '/our-works';
  const showSecondary = Boolean(secondaryLabel);

  return (
    <section className="bg-background py-16 lg:py-20" data-grv-cta-card>
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="rounded-2xl border border-gold/35 bg-card px-6 py-12 sm:px-10 sm:py-14 text-center shadow-[0_0_0_1px_rgba(201,162,39,0.08)]">
          <p className="text-gold text-xs font-bold uppercase tracking-[0.18em] mb-4">
            {eyebrow}
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-foreground leading-tight mb-5">
            {title}
          </h2>
          <div className="w-14 h-0.5 bg-gold-gradient mx-auto mb-6" />
          {text ? (
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed max-w-2xl mx-auto mb-8">
              {text}
            </p>
          ) : null}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <AppLink
              href={primaryHref}
              className="inline-flex items-center justify-center gap-2 bg-gold-gradient text-dark font-bold px-7 py-3.5 rounded-md hover:opacity-90 transition-opacity text-sm uppercase tracking-wider min-w-[200px]"
            >
              <Phone className="w-4 h-4" />
              {primaryLabel}
            </AppLink>
            {showSecondary ? (
              <AppLink
                href={secondaryHref}
                className="inline-flex items-center justify-center gap-2 border border-border text-foreground hover:border-gold hover:text-gold font-semibold px-7 py-3.5 rounded-md transition-colors text-sm uppercase tracking-wider min-w-[200px]"
              >
                {secondaryLabel} <ArrowRight className="w-4 h-4" />
              </AppLink>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
